import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { supabase } from '../supabase';
import { COMMON_AVOIDS, normaliseAvoidTags } from '../utils/profileOptions';

const PRODUCT_TOUR_STORAGE_KEY = 'sonmi_has_seen_product_tour';
export const GUEST_PROFILE_KEY = 'sonmi_guest_profile';

// Brand type — matches the Welcome screen (editorial serif headlines, DM Sans body).
const SERIF = '"DM Sans", system-ui, sans-serif';
const SANS = '"DM Sans", system-ui, sans-serif';

export function getHasSeenProductTour() {
  try {
    return localStorage.getItem(PRODUCT_TOUR_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function clearHasSeenProductTour() {
  try {
    localStorage.removeItem(PRODUCT_TOUR_STORAGE_KEY);
  } catch {}
}

export function setHasSeenProductTour() {
  try {
    localStorage.setItem(PRODUCT_TOUR_STORAGE_KEY, 'true');
  } catch {}
}

export function getGuestProfile() {
  try {
    const raw = localStorage.getItem(GUEST_PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearGuestProfile() {
  try {
    localStorage.removeItem(GUEST_PROFILE_KEY);
  } catch {}
}

const AGE_OPTIONS = ['Under 18', '18–24', '25–34', '35–44', '45–54', '55+'];

// What's brought the user to Son:mi — the highest-signal "know the user" question.
const INTENT_OPTIONS = [
  'A routine that works for me',
  'Specific concerns to fix',
  'Shade matching for makeup',
  'Just exploring',
];

// Their main skin concerns — feeds the AI's personalised recommendations.
const CONCERN_OPTIONS = [
  'Breakouts',
  'Dryness',
  'Sensitivity / redness',
  'Dark spots & uneven tone',
  'Texture & pores',
  'Fine lines',
  'Dullness',
  'Oiliness',
];

// Sensitivities list lives in src/utils/profileOptions.js so the profile
// editor uses the exact same canonical labels (so "Fragrance" in onboarding
// matches "Fragrance" in profile matches the string the backend compares).

// Cap concerns to keep recommendations focused — research-backed; the user gets
// a friendly nudge instead of being blocked silently.
const CONCERN_LIMIT = 3;

// Slides: 3 educational + age + intent + concerns + sensitivities + selfie CTA
const TOTAL_SLIDES = 8;

export function FirstTimeOverview({ isOpen, onClose, onComplete, onTriggerUpload, userId, isAnonymous }) {
  const [step, setStep] = useState(0);
  const [mounted, setMounted] = useState(false);
  const touchStartX = useRef(null);

  // Question answers
  const [age, setAge] = useState('');
  const [intent, setIntent] = useState([]);
  const [intentOther, setIntentOther] = useState('');
  const [concerns, setConcerns] = useState([]);
  const [concernOther, setConcernOther] = useState('');
  const [concernNote, setConcernNote] = useState(''); // cap-at-3 nudge
  // Sensitivities = "things to avoid in products". Combobox input:
  // scrollable suggestions + freetext. Empty is a valid skip.
  const [sensitivityTags, setSensitivityTags] = useState([]);
  const [sensitivityDraft, setSensitivityDraft] = useState('');
  const [sensitivityDropdownOpen, setSensitivityDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setStep(0);
      setAge('');
      setIntent([]); setIntentOther('');
      setConcerns([]); setConcernOther(''); setConcernNote('');
      setSensitivityTags([]); setSensitivityDraft(''); setSensitivityDropdownOpen(false);
    } else {
      setMounted(false);
    }
  }, [isOpen]);

  const toggleIntent = (label) => {
    setIntent(prev => prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label]);
  };
  const toggleConcern = (label) => {
    setConcerns(prev => {
      if (prev.includes(label)) { setConcernNote(''); return prev.filter(x => x !== label); }
      if (prev.length >= CONCERN_LIMIT) {
        setConcernNote(`Try focusing on up to ${CONCERN_LIMIT} — we can address more once we see how your skin responds.`);
        return prev;
      }
      setConcernNote('');
      return [...prev, label];
    });
  };

  // Tag helpers — mirror PersonalProfile.jsx so the two flows behave the same.
  const addSensitivityTag = (raw) => {
    const trimmed = (raw || '').trim().slice(0, 60);
    if (!trimmed) return;
    setSensitivityTags((prev) => {
      const exists = prev.some((t) => t.toLowerCase() === trimmed.toLowerCase());
      return exists ? prev : [...prev, trimmed];
    });
    setSensitivityDraft('');
  };
  const removeSensitivityTag = (tag) => {
    setSensitivityTags((prev) => prev.filter((t) => t !== tag));
  };
  const onSensitivityKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addSensitivityTag(sensitivityDraft);
    } else if (e.key === 'Backspace' && !sensitivityDraft && sensitivityTags.length > 0) {
      e.preventDefault();
      removeSensitivityTag(sensitivityTags[sensitivityTags.length - 1]);
    }
  };

  const canProceed = () => {
    // New flow: 0-2 educational · 3 age · 4 intent · 5 concerns · 6 sensitivities · 7 selfie CTA
    // Steps 4 & 5 accept EITHER a selected option OR free text typed in the
    // "other" box — a typed answer is a valid answer on its own, no need to also
    // tick a chip. handleNext() already folds the free text into the saved answers.
    if (step === 3) return !!age;
    if (step === 4) return intent.length > 0 || intentOther.trim().length > 0;
    if (step === 5) return concerns.length > 0 || concernOther.trim().length > 0;
    return true;
  };

  const handleNext = async () => {
    if (step < TOTAL_SLIDES - 2) {
      setStep((s) => s + 1);
    } else if (step === TOTAL_SLIDES - 2) {
      await handleSave();
      setStep(TOTAL_SLIDES - 1);
    }
  };

  const handlePrev = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  const handleSkip = () => {
    setHasSeenProductTour();
    onClose();
  };

  const buildAnswers = () => {
    const concernList = concernOther.trim()
      ? [...concerns, concernOther.trim()]
      : concerns;
    return {
      age_range: age,
      primary_concerns: concernList,
      sensitivities: buildSensitivities(),
      // Intent + concerns (with their free-text additions) mirror into
      // answered_questions for richer AI context; primary_concerns also lives
      // in its own column for the rubric / recommendation paths.
      answered_questions: {
        intent,
        intent_other: intentOther.trim() || null,
        concerns: concernList,
        onboarded_at: new Date().toISOString(),
      },
    };
  };

  const handleFinish = () => {
    setHasSeenProductTour();
    onComplete?.(buildAnswers());
  };

  const buildSensitivities = () => {
    // Fold in any uncommitted draft so users who tap Continue mid-typing
    // don't lose what they wrote. Empty = un-answered (no special sentinel).
    const draft = sensitivityDraft.trim();
    return normaliseAvoidTags(draft ? [...sensitivityTags, draft] : sensitivityTags);
  };

  const handleSave = async () => {
    const answers = buildAnswers();
    if (userId && !isAnonymous) {
      setSaving(true);
      try {
        await supabase.from('user_profiles').upsert(
          { user_id: userId, ...answers },
          { onConflict: 'user_id' }
        );
      } catch (err) {
        console.error('Tour save error:', err);
      } finally {
        setSaving(false);
      }
    } else {
      try {
        localStorage.setItem(GUEST_PROFILE_KEY, JSON.stringify(answers));
      } catch {}
    }
  };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0 && step < TOTAL_SLIDES - 1 && canProceed()) handleNext();
      else if (diff < 0 && step > 0) handlePrev();
    }
    touchStartX.current = null;
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col transition-opacity duration-300 ${
        mounted ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ background: '#F2EAE1' }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slide content — a centered column that fills the whole screen */}
      <div className="flex-1 overflow-y-auto">
        <div className="w-full max-w-md mx-auto min-h-full flex flex-col justify-start pt-[15vh] pb-8">
          {renderSlide(step, {
            age, setAge,
            intent, toggleIntent, intentOther, setIntentOther,
            concerns, toggleConcern, concernOther, setConcernOther, concernNote,
            sensitivityTags, sensitivityDraft, setSensitivityDraft,
            sensitivityDropdownOpen, setSensitivityDropdownOpen,
            addSensitivityTag, removeSensitivityTag, onSensitivityKeyDown,
          })}
        </div>
      </div>

      {/* Controls pinned to the bottom */}
      <div className="flex-shrink-0">
        <div className="w-full max-w-md mx-auto px-8 pt-2 pb-9">
          {/* Slim progress bar */}
          <div className="h-[3px] rounded-full mb-7" style={{ background: '#E4DCD2' }}>
            <div className="h-full rounded-full transition-all duration-300" style={{ background: '#7A1F1F', width: `${((step + 1) / TOTAL_SLIDES) * 100}%` }} />
          </div>

          {/* Last slide buttons */}
          {step === TOTAL_SLIDES - 1 ? (
            <div className="space-y-3">
              <button
                onClick={() => { handleFinish(); onTriggerUpload?.(); }}
                className="w-full rounded-full py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white"
                style={{ background: '#1F1310', fontFamily: SANS }}
              >
                Upload a selfie
              </button>
              <button
                onClick={handleFinish}
                className="w-full rounded-full py-4 text-[13px] font-semibold uppercase tracking-[0.14em]"
                style={{ background: 'transparent', border: '1px solid #C9B8A4', color: '#2A1212', fontFamily: SANS }}
              >
                Skip for now
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {step > 0 && (
                <button
                  onClick={handlePrev}
                  aria-label="Back"
                  className="w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center"
                  style={{ border: '1px solid #C9B8A4', color: '#2A1212' }}
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={!canProceed() || saving}
                className="flex-1 rounded-full py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white transition-opacity"
                style={{ background: '#1F1310', fontFamily: SANS, opacity: (canProceed() && !saving) ? 1 : 0.35 }}
              >
                {saving ? 'Saving…' : step === TOTAL_SLIDES - 2 ? 'Done' : 'Continue'}
              </button>
            </div>
          )}

          {step < TOTAL_SLIDES - 1 && (
            <button
              onClick={handleSkip}
              className="w-full mt-4 text-center"
              style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 15, color: '#5B4040', opacity: 0.85 }}
            >
              Skip for now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Editorial selection row — replaces the oval pill chips. Full-width, label
// left-aligned, a small maroon check on the right when selected. Hairline
// divider below. Works for single-select (age) and multi-select.
function OptionRow({ label, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between px-1 py-4 text-left border-b border-[#EAE0D4] last:border-b-0 hover:bg-[#FAF4EB] transition-colors"
    >
      <span className="text-[15px]" style={{ fontFamily: SANS, color: selected ? '#2A1212' : 'rgba(42, 50, 75, 0.78)', fontWeight: selected ? 500 : 400 }}>{label}</span>
      {selected ? (
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="#7A1F1F" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
      ) : (
        <span className="w-4 h-4 flex-shrink-0 rounded-full" style={{ border: '1px solid #D9CDB8' }} />
      )}
    </button>
  );
}

function renderSlide(step, state) {
  const {
    age, setAge,
    intent, toggleIntent, intentOther, setIntentOther,
    concerns, toggleConcern, concernOther, setConcernOther, concernNote,
    sensitivityTags, sensitivityDraft, setSensitivityDraft,
    sensitivityDropdownOpen, setSensitivityDropdownOpen,
    addSensitivityTag, removeSensitivityTag, onSensitivityKeyDown,
  } = state;

  switch (step) {
    case 0:
      return (
        <SlideShell
          eyebrow="Ask anything"
          title={<>Skincare, in<br />conversation.</>}
          subtitle="Ask about ingredients, routines, or what to avoid — and get answers shaped around your skin, not generic advice."
        />
      );

    case 1:
      return (
        <SlideShell
          eyebrow="Shade match"
          title={<>Matched to your<br />exact tone.</>}
          subtitle="One selfie, and Son:mi finds your shade across foundation, lipstick and blush — read from your real undertone."
        />
      );

    case 2:
      return (
        <SlideShell
          eyebrow="Product scans"
          title={<>Know before<br />you buy.</>}
          subtitle="Scan any product and see how its ingredients fit your skin — in seconds, with nothing to read off the label yourself."
        />
      );

    case 3:
      return (
        <SlideShell eyebrow="About you" title="How old are you?" subtitle="A few quick questions so Son:mi can personalise everything to you.">
          <div className="mt-7 border-t border-[#EAE0D4]">
            {AGE_OPTIONS.map((option) => (
              <OptionRow key={option} label={option} selected={age === option} onClick={() => setAge(option)} />
            ))}
          </div>
        </SlideShell>
      );

    case 4:
      // What brings you here — the highest-signal "know the user" question.
      return (
        <SlideShell eyebrow="About you" title={<>What brings you<br />here?</>} subtitle="Pick anything that fits — you can change later.">
          <div className="mt-6 border-t border-[#EAE0D4]">
            {INTENT_OPTIONS.map((label) => (
              <OptionRow key={label} label={label} selected={intent.includes(label)} onClick={() => toggleIntent(label)} />
            ))}
          </div>
          {/* Free-text catch-all — the chips can't cover every reason someone shows up. */}
          <div className="mt-5">
            <p className="text-[13px] font-semibold uppercase tracking-wider text-[#2A324B]/45 mb-2">In your own words <span className="opacity-60 normal-case font-normal tracking-normal">(optional)</span></p>
            <textarea
              value={intentOther}
              onChange={(e) => setIntentOther(e.target.value)}
              placeholder="e.g. preparing skin for a wedding, recovering from a procedure…"
              rows={2}
              className="w-full px-4 py-3 rounded-2xl border border-[#EAE0D4] text-[13px] text-[#2A1212] placeholder-[#2A324B]/35 focus:outline-none focus:border-[#7A1F1F]/40 bg-white resize-none"
            />
          </div>
        </SlideShell>
      );

    case 5:
      // Main concerns — feeds the AI's personalised recommendations + rubric.
      // Capped at 3 so the plan stays focused; gentle nudge instead of a block.
      return (
        <SlideShell eyebrow="About you" title={<>What are you trying<br />to improve?</>} subtitle={`Pick up to ${CONCERN_LIMIT} — focusing on a few makes the plan stronger.`}>
          <div className="mt-6 border-t border-[#EAE0D4]">
            {CONCERN_OPTIONS.map((label) => (
              <OptionRow key={label} label={label} selected={concerns.includes(label)} onClick={() => toggleConcern(label)} />
            ))}
          </div>
          {concernNote && (
            <p className="mt-3 text-[13px] text-[#7A1F1F] leading-relaxed">{concernNote}</p>
          )}
          <div className="mt-5">
            <p className="text-[13px] font-semibold uppercase tracking-wider text-[#2A324B]/45 mb-2">Something else <span className="opacity-60 normal-case font-normal tracking-normal">(optional)</span></p>
            <input
              type="text"
              value={concernOther}
              onChange={(e) => setConcernOther(e.target.value)}
              placeholder="e.g. milia, post-acne marks, perioral dermatitis…"
              className="w-full px-4 py-3 rounded-2xl border border-[#EAE0D4] text-[13px] text-[#2A1212] placeholder-[#2A324B]/35 focus:outline-none focus:border-[#7A1F1F]/40 bg-white"
            />
          </div>
        </SlideShell>
      );

    case 6:
      // Things-to-avoid as a combobox: scrollable suggestions, filter-as-you-
      // type, freetext "Add ..." fallback. Empty is a valid skip.
      return (
        <SlideShell eyebrow="About you" title={<>Ingredients<br />to avoid?</>} subtitle="Allergies, sensitivities, or anything you avoid.">
          <div className="mt-6">
            {/* Existing tags row, if any. */}
            {sensitivityTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {sensitivityTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-2 pl-4 pr-2 py-2 rounded-full bg-[#7A1F1F] text-white text-sm font-medium"
                    style={{ fontFamily: SANS }}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeSensitivityTag(tag)}
                      className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-white/15 transition-colors"
                      aria-label={`Remove ${tag}`}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="relative">
              <input
                type="text"
                value={sensitivityDraft}
                onChange={(e) => { setSensitivityDraft(e.target.value); setSensitivityDropdownOpen(true); }}
                onFocus={() => setSensitivityDropdownOpen(true)}
                onKeyDown={onSensitivityKeyDown}
                onBlur={() => {
                  // Delay so a click on a dropdown option fires first.
                  setTimeout(() => {
                    const draft = (sensitivityDraft || '').trim();
                    if (draft) addSensitivityTag(draft);
                    setSensitivityDropdownOpen(false);
                  }, 180);
                }}
                placeholder="Search or type your own…"
                className="w-full px-4 py-3 rounded-2xl border border-[#EAE0D4] text-[13px] text-[#2A1212] placeholder-[#2A324B]/35 focus:outline-none focus:border-[#7A1F1F]/40 bg-white pr-10"
                style={{ fontFamily: SANS }}
              />
              {!sensitivityDropdownOpen && (
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2A324B]/40 pointer-events-none" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              )}

              {sensitivityDropdownOpen && (() => {
                const query = (sensitivityDraft || '').trim().toLowerCase();
                const already = new Set(sensitivityTags.map((t) => t.toLowerCase()));
                const suggestions = COMMON_AVOIDS.filter((opt) =>
                  !already.has(opt.toLowerCase()) &&
                  (!query || opt.toLowerCase().includes(query))
                );
                const exactMatch = suggestions.some((s) => s.toLowerCase() === query);
                const showAddCustom = query && !exactMatch && !already.has(query);
                if (suggestions.length === 0 && !showAddCustom) return null;
                return (
                  <div className="absolute top-full left-0 right-0 mt-1 z-20 max-h-60 overflow-y-auto rounded-2xl border border-[#EAE0D4] bg-white shadow-lg">
                    {suggestions.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); addSensitivityTag(opt); }}
                        className="w-full text-left px-4 py-2.5 text-[13px] text-[#2A1212] hover:bg-[#FAF4EB] transition-colors border-b border-[#F0EAE0] last:border-b-0"
                        style={{ fontFamily: SANS }}
                      >
                        {opt}
                      </button>
                    ))}
                    {showAddCustom && (
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); addSensitivityTag(sensitivityDraft); }}
                        className="w-full text-left px-4 py-2.5 text-[13px] text-[#2A1212] hover:bg-[#FAF4EB] transition-colors font-medium flex items-center gap-2"
                        style={{ fontFamily: SANS }}
                      >
                        <span className="text-[#7A1F1F]">+</span>
                        Add "{sensitivityDraft.trim()}"
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </SlideShell>
      );

    case 7:
      return (
        <SlideShell
          eyebrow="Your skin"
          title={<>Now, let's understand<br />your skin.</>}
          subtitle="Upload a selfie and Son:mi understands your skin type, tone and concerns — the foundation for everything it recommends."
        />
      );

    default:
      return null;
  }
}

function SlideShell({ eyebrow, title, subtitle, children }) {
  // Editorial header matching the Welcome screen: small uppercase maroon eyebrow,
  // large Cormorant serif headline, quiet DM Sans sub-line. Left-aligned.
  return (
    <div className="px-8">
      {eyebrow && (
        <p className="mb-4" style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#7A1F1F' }}>
          {eyebrow}
        </p>
      )}
      <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 42, lineHeight: 1.08, letterSpacing: '-0.01em', color: '#2A1212', margin: 0 }}>
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4" style={{ fontFamily: SANS, fontSize: 15, lineHeight: 1.5, color: '#2A324B', opacity: 0.72, maxWidth: 360, margin: '16px 0 0' }}>
          {subtitle}
        </p>
      )}
      {children}
    </div>
  );
}

function SlideDesc({ children }) {
  return (
    <p className="mt-5" style={{ fontFamily: SANS, fontSize: 15, lineHeight: 1.5, color: '#2A324B', opacity: 0.72, maxWidth: 360 }}>
      {children}
    </p>
  );
}

export default FirstTimeOverview;
